import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import {
  NovaDatepicker,
  NovaDatepickerChangeEvent,
} from "@rds-nova/angular-datepicker";
import { NovaIcon } from "@rds-nova/angular-icon";
import { toBoardWallClock } from "../../ron-closing-monitor.utils";
import { RonMonitorClock } from "../../services/ron-monitor-clock.service";

let nextPopoverId = 0;

@Component({
  selector: "app-monitor-date-picker",
  templateUrl: "./monitor-date-picker.component.html",
  styleUrls: ["./monitor-date-picker.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NovaDatepicker, NovaIcon],
  host: {
    class: "rcm-date-picker",
    "(keydown.escape)": "onEscape($event)",
    "(document:click)": "onDocumentClick($event)",
  },
})
export class MonitorDatePickerComponent implements OnChanges {
  @Input({ required: true }) value!: Date;

  @Output() dateChange = new EventEmitter<Date>();

  @ViewChild(NovaDatepicker) private picker?: NovaDatepicker;
  @ViewChild("toggleButton")
  private toggleButton?: ElementRef<HTMLButtonElement>;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly clock = inject(RonMonitorClock);

  readonly popoverId = `rcm-date-picker-${nextPopoverId++}`;

  open = false;
  today = this.boardToday();

  ngOnChanges(): void {
    this.seed();
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.today = this.boardToday();
      this.seed();
      this.picker?.focusCalendarDay();
    }
  }

  close(): void {
    this.open = false;
  }

  onDateSelect(event: NovaDatepickerChangeEvent): void {
    if (!event.value) {
      return;
    }
    this.dateChange.emit(event.value);
    this.close();
    this.focusToggle();
  }

  onEscape(event: Event): void {
    if (!this.open) {
      return;
    }
    event.stopPropagation();
    this.close();
    this.focusToggle();
  }

  onDocumentClick(event: Event): void {
    if (!this.open) {
      return;
    }
    const target = event.target;
    if (target instanceof Node && this.host.nativeElement.contains(target)) {
      return;
    }
    this.close();
  }

  private seed(): void {
    const picker = this.picker;
    if (!picker || !this.value) {
      return;
    }
    picker.selectedDate.set(this.value);
    picker.goToDate(this.value);
  }

  private focusToggle(): void {
    this.toggleButton?.nativeElement.focus();
  }

  private boardToday(): Date {
    return toBoardWallClock(this.clock.now());
  }
}
